/**
 * Pure helpers of the inspector (W2-06; DESIGN.md §4.11, §4.21, §5.5, §6.4, §6.7, §6.8, rpe-format F§9-F§12).
 *
 * Everything here is a function of document data: aggregation of multi-selections into mixed field values, the
 * derived line type and what a type change removes, cycle-proof parent options, name patterns, text segment modes,
 * segment exactness, note-control evaluation and presets, the song-select preview range check and display-only
 * curve evaluation. No React, no services: unit-tested in model.test.ts.
 */

import { Ease, HOLD, LINEAR } from '../../core/ease/index';
import type { EaseUse, Ease as EaseValue } from '../../core/ease/types';
import { isGifAsset, kindOfLine } from '../../core/model/lineKind';
import { PROP_SPECS, propAppliesTo } from '../../core/model/props';
import type {
  AssetRef,
  ChartDoc,
  ControlCurve,
  ControlPoint,
  DocIndexes,
  Key,
  KeyId,
  Line,
  LineId,
  LineKind,
  LineProp,
  Note,
  NoteKind,
  Track,
  TrackId,
  UiElement,
  UnitContext,
} from '../../core/model/types';
import type { LineTypeChoice } from '../../core/commands/types';
import { textSegmentMode, type TextSegmentMode } from '../../core/commands/text';
import { Beats } from '../../core/time/beat';
import type { Beat, Sec, TempoMap } from '../../core/time/types';
import type { RGB } from '../../core/util/types';
import type { FieldValue } from '../../ui/types';
import type { Selection } from '../../state/types';

// ---------------------------------------------------------------------------------------------------------------
// Aggregation (mixed values)
// ---------------------------------------------------------------------------------------------------------------

/** The common value of `values`, `{ kind: 'mixed' }` when they differ, null when there are none. */
export function fieldOf<T>(values: Iterable<T>, eq: (a: T, b: T) => boolean = Object.is): FieldValue<T> | null {
  let first: { readonly v: T } | null = null;
  for (const v of values) {
    if (first === null) first = { v };
    else if (!eq(first.v, v)) return { kind: 'mixed' };
  }
  return first === null ? null : { kind: 'value', value: first.v };
}

/** Like `fieldOf`, with a fallback value for an empty list. */
export function fieldOr<T>(values: Iterable<T>, fallback: T, eq?: (a: T, b: T) => boolean): FieldValue<T> {
  return fieldOf(values, eq) ?? { kind: 'value', value: fallback };
}

export function rgbEq(a: RGB | null, b: RGB | null): boolean {
  if (a === null || b === null) return a === b;
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

export function beatEq(a: Beat, b: Beat): boolean {
  return Beats.eq(a, b);
}

/** A colour field over optional colours: null when none is set, mixed when they differ (or only some are set). */
export function colorFieldOf(values: readonly (RGB | null)[]): FieldValue<RGB> | null {
  if (values.every((v) => v === null)) return null;
  if (values.some((v) => v === null)) return { kind: 'mixed' };
  return fieldOf(values as readonly RGB[], rgbEq);
}

/** Maps a field value (unit conversion for display). */
export function mapField<A, B>(v: FieldValue<A>, f: (a: A) => B): FieldValue<B> {
  return v.kind === 'mixed' ? v : { kind: 'value', value: f(v.value) };
}

/** True/false/'mixed' for checkboxes. */
export function checkOf(values: Iterable<boolean>): boolean | 'mixed' {
  const f = fieldOf(values);
  if (f === null) return false;
  return f.kind === 'mixed' ? 'mixed' : f.value;
}

/** Shallow equality of two arrays (selector results). */
export function arrayEq<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (!Object.is(a[i], b[i])) return false;
  return true;
}

// ---------------------------------------------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------------------------------------------

export const NOTE_KINDS: readonly NoteKind[] = ['tap', 'drag', 'flick', 'hold'];

export function kindCounts(notes: readonly Note[]): Readonly<Record<NoteKind, number>> {
  const out: Record<NoteKind, number> = { tap: 0, drag: 0, flick: 0, hold: 0 };
  for (const n of notes) out[n.kind]++;
  return out;
}

/**
 * One field over many items in a single pass (the inspector shows 50k-note selections): the first value, and
 * whether a later one differs.
 */
class FieldAcc<T> {
  private first: T | undefined = undefined;
  private has = false;
  private differs = false;
  private readonly eq: (a: T, b: T) => boolean;
  constructor(eq: (a: T, b: T) => boolean = Object.is) {
    this.eq = eq;
  }
  add(v: T): void {
    if (!this.has) {
      this.has = true;
      this.first = v;
    } else if (!this.differs && !this.eq(this.first as T, v)) this.differs = true;
  }
  get mixed(): boolean {
    return this.differs;
  }
  result(): FieldValue<T> | null {
    if (!this.has) return null;
    return this.differs ? { kind: 'mixed' } : { kind: 'value', value: this.first as T };
  }
}

/** Every field the note inspector shows, aggregated over the selected notes in ONE pass (checklist W3-06). */
export interface NoteAggregate {
  readonly kind: FieldValue<NoteKind> | null;
  readonly time: FieldValue<Beat> | null;
  readonly end: FieldValue<Beat> | null;
  readonly holds: readonly Note[];
  readonly x: FieldValue<number> | null;
  readonly above: FieldValue<boolean> | null;
  readonly fake: boolean | 'mixed';
  readonly speed: FieldValue<number> | null;
  readonly yOffset: FieldValue<number> | null;
  readonly size: FieldValue<number> | null;
  readonly alpha: FieldValue<number> | null;
  readonly judgeArea: FieldValue<number> | null;
  readonly visibleTime: FieldValue<number | null> | null;
  readonly allTimed: boolean;
  readonly anyTimed: boolean;
  /** Hitsound key ("default", "builtin:tap", "asset:<sha>"). */
  readonly hitsound: FieldValue<string> | null;
  readonly tint: FieldValue<RGB> | null;
  readonly tintHit: FieldValue<RGB> | null;
  readonly judgeSet: boolean;
  /** Some "More" field differs from its default. */
  readonly moreInUse: boolean;
  /** The earliest start beat (null without notes). */
  readonly earliest: Beat | null;
}

function colorAcc(acc: FieldAcc<RGB>, state: { some: boolean; none: boolean }, v: RGB | null): void {
  if (v === null) state.none = true;
  else {
    state.some = true;
    acc.add(v);
  }
}

function colorResult(acc: FieldAcc<RGB>, state: { some: boolean; none: boolean }): FieldValue<RGB> | null {
  if (!state.some) return null;
  if (state.none) return { kind: 'mixed' };
  return acc.result();
}

/** Aggregates the note fields in one pass; `hitsoundKey` turns a note's hitsound into its select value. */
export function aggregateNotes(notes: readonly Note[], hitsoundKey: (h: Note['hitsound']) => string): NoteAggregate {
  const kind = new FieldAcc<NoteKind>();
  const time = new FieldAcc<Beat>(beatEq);
  const end = new FieldAcc<Beat>(beatEq);
  const x = new FieldAcc<number>();
  const above = new FieldAcc<boolean>();
  const fake = new FieldAcc<boolean>();
  const speed = new FieldAcc<number>();
  const yOffset = new FieldAcc<number>();
  const size = new FieldAcc<number>();
  const alpha = new FieldAcc<number>();
  const judge = new FieldAcc<number>();
  const visible = new FieldAcc<number | null>();
  const hitsound = new FieldAcc<string>();
  const tint = new FieldAcc<RGB>(rgbEq);
  const tintHit = new FieldAcc<RGB>(rgbEq);
  const tintState = { some: false, none: false };
  const tintHitState = { some: false, none: false };
  const holds: Note[] = [];
  let timed = 0;
  let judgeSet = false;
  let more = false;
  let earliest: Beat | null = null;
  for (const n of notes) {
    kind.add(n.kind);
    time.add(n.t);
    if (n.kind === 'hold') {
      holds.push(n);
      end.add(n.end);
    }
    x.add(n.x);
    above.add(n.above);
    fake.add(n.fake);
    speed.add(n.speed);
    yOffset.add(n.yOffset);
    size.add(n.size);
    alpha.add(n.alpha);
    judge.add(n.judgeArea ?? 1);
    visible.add(n.visibleTime);
    if (n.visibleTime !== null) timed++;
    hitsound.add(hitsoundKey(n.hitsound));
    colorAcc(tint, tintState, n.tint);
    colorAcc(tintHit, tintHitState, n.tintHitEffects);
    if (n.judgeArea !== null) judgeSet = true;
    if (
      !more &&
      (n.speed !== 1 ||
        n.yOffset !== 0 ||
        n.size !== 1 ||
        n.alpha < 255 ||
        n.visibleTime !== null ||
        n.hitsound !== null ||
        n.tint !== null ||
        n.tintHitEffects !== null ||
        n.judgeArea !== null)
    )
      more = true;
    if (earliest === null || Beats.lt(n.t, earliest)) earliest = n.t;
  }
  const fakeField = fake.result();
  return {
    kind: kind.result(),
    time: time.result(),
    end: end.result(),
    holds,
    x: x.result(),
    above: above.result(),
    fake: fakeField === null ? false : fakeField.kind === 'mixed' ? 'mixed' : fakeField.value,
    speed: speed.result(),
    yOffset: yOffset.result(),
    size: size.result(),
    alpha: alpha.result(),
    judgeArea: judge.result(),
    visibleTime: visible.result(),
    allTimed: notes.length > 0 && timed === notes.length,
    anyTimed: timed > 0,
    hitsound: hitsound.result(),
    tint: colorResult(tint, tintState),
    tintHit: colorResult(tintHit, tintHitState),
    judgeSet,
    moreInUse: more,
    earliest,
  };
}

/** Notes in the order per-item expressions see them (t, x, id), like Core.notes.exprNotes. */
export function notesInTimeOrder(notes: readonly Note[]): Note[] {
  return [...notes].sort((a, b) => Beats.cmp(a.t, b.t) || a.x - b.x || a.id - b.id);
}

/** Chart seconds of a note exactly as Phira computes them (raw imported triple when present). */
export function noteSec(tempo: TempoMap, note: Note): Sec {
  const raw = note.src?.rawStart;
  return raw === undefined ? tempo.secAt(note.t) : tempo.secAtTriple(raw);
}

/**
 * How many OTHER notes share this note's bit-identical hit time anywhere in the chart (Phira multi-highlight,
 * F§11.8: fakes and hold heads count).
 */
export function simultaneousCount(doc: Pick<ChartDoc, 'notes'>, tempo: TempoMap, note: Note): number {
  const sec = noteSec(tempo, note);
  let n = 0;
  for (const other of doc.notes.values()) {
    if (other.id !== note.id && noteSec(tempo, other) === sec) n++;
  }
  return n;
}

// ---------------------------------------------------------------------------------------------------------------
// Lines: type, data loss, parents, names
// ---------------------------------------------------------------------------------------------------------------

export const LINE_TYPES: readonly LineTypeChoice[] = ['line', 'text', 'image', 'gif', 'paint', 'uiMover'];
/** Types offered at the Guided level (D§2.4); the others are shown with a Full tag. */
export const GUIDED_LINE_TYPES: ReadonlySet<LineTypeChoice> = new Set<LineTypeChoice>(['line', 'text', 'image']);

export const UI_ELEMENT_LIST: readonly UiElement[] = ['pause', 'combonumber', 'combo', 'score', 'bar', 'name', 'level'];

/** The line type the Type dropdown shows (derived kind, UI mover first). */
export function lineTypeOf(
  doc: Pick<ChartDoc, 'assets'>,
  idx: Pick<DocIndexes, 'tracksOfLine'>,
  line: Line,
): LineTypeChoice {
  if (line.attachUI !== null) return 'uiMover';
  return typeOfKind(kindOfLine(doc, idx, line));
}

export function typeOfKind(kind: LineKind): LineTypeChoice {
  switch (kind) {
    case 'normal':
      return 'line';
    case 'texture':
      return 'image';
    case 'gif':
      return 'gif';
    case 'text':
      return 'text';
    case 'paint':
      return 'paint';
  }
}

export type LossItem =
  | { readonly kind: 'texture' }
  | { readonly kind: 'text' | 'paint' | 'gif'; readonly keys: number }
  | { readonly kind: 'autoLoop' }
  | { readonly kind: 'attachUI' };

/** Number of keys of a line property over its layers (0 when absent). */
export function propKeyCount(
  idx: Pick<DocIndexes, 'tracksOfLine' | 'keysByTrack'>,
  line: LineId,
  prop: LineProp,
): number {
  let n = 0;
  for (const t of idx.tracksOfLine(line).get(prop) ?? []) n += idx.keysByTrack(t).length;
  return n;
}

/**
 * What `Core.lines.setLineType` removes when the line becomes `target` (mirrors its rules): the texture, the text /
 * paint / GIF progress tracks, "Play automatically" and the HUD link. Empty = the change loses nothing.
 */
export function typeChangeLoss(
  idx: Pick<DocIndexes, 'tracksOfLine' | 'keysByTrack'>,
  line: Line,
  target: LineTypeChoice,
): readonly LossItem[] {
  const out: LossItem[] = [];
  const keep: Readonly<Record<LineTypeChoice, readonly ('text' | 'paint' | 'gif')[]>> = {
    line: [],
    text: ['text'],
    paint: ['paint'],
    image: [],
    gif: ['gif'],
    uiMover: [],
  };
  if (line.texture !== null && (target === 'line' || target === 'text' || target === 'paint' || target === 'uiMover')) {
    out.push({ kind: 'texture' });
  }
  for (const prop of ['text', 'paint', 'gif'] as const) {
    if (keep[target].includes(prop)) continue;
    const keys = propKeyCount(idx, line.id, prop);
    if (keys > 0) out.push({ kind: prop, keys });
  }
  if (line.gifAutoLoop && target !== 'gif') out.push({ kind: 'autoLoop' });
  if (line.attachUI !== null && target !== 'uiMover') out.push({ kind: 'attachUI' });
  return out;
}

/** True for assets a line can show as an image (texture, GIF or illustration; any image MIME type). */
export function isImageAssetRef(a: AssetRef): boolean {
  if (a.role === 'texture' || a.role === 'gif' || a.role === 'illustration') return true;
  return a.mime.startsWith('image/');
}

/** Image assets of the project, sorted by path; `gifOnly` keeps GIFs Phira decodes. */
export function imageAssets(doc: Pick<ChartDoc, 'assets'>, gifOnly = false): AssetRef[] {
  const out: AssetRef[] = [];
  for (const a of doc.assets.values()) {
    if (a.missing === true || !isImageAssetRef(a)) continue;
    if (gifOnly && !isGifAsset(a)) continue;
    out.push(a);
  }
  return out.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
}

/** Sound assets usable as custom hitsounds (Phira plays its built-in sound for tap/drag/flick.mp3). */
export function soundAssets(doc: Pick<ChartDoc, 'assets'>): AssetRef[] {
  const reserved = new Set(['tap.mp3', 'drag.mp3', 'flick.mp3']);
  const out: AssetRef[] = [];
  for (const a of doc.assets.values()) {
    if (a.missing === true || reserved.has(a.path)) continue;
    if (a.role === 'hitsound' || (a.role === 'other' && a.mime.startsWith('audio/'))) out.push(a);
  }
  return out.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
}

/** Every descendant of `id` (children, grandchildren, ...). */
export function descendantsOf(idx: Pick<DocIndexes, 'childrenOf'>, id: LineId): Set<LineId> {
  const out = new Set<LineId>();
  const stack = [...idx.childrenOf(id)];
  while (stack.length > 0) {
    const c = stack.pop() as LineId;
    if (out.has(c)) continue;
    out.add(c);
    stack.push(...idx.childrenOf(c));
  }
  return out;
}

export interface ParentOption {
  readonly id: LineId;
  readonly name: string;
  readonly index: number;
  /** Why the line cannot be the parent of the edited lines: one of them ('self') or a descendant ('loop'). */
  readonly blocked: 'self' | 'loop' | null;
}

/** Parent picker entries in export order: lines that would create a cycle are listed but blocked. */
export function parentOptions(
  doc: Pick<ChartDoc, 'lines' | 'lineOrder'>,
  idx: Pick<DocIndexes, 'childrenOf'>,
  lines: readonly LineId[],
): ParentOption[] {
  const self = new Set(lines);
  const loop = new Set<LineId>();
  for (const l of lines) for (const d of descendantsOf(idx, l)) loop.add(d);
  return doc.lineOrder.map((id, index) => {
    const rec = doc.lines.get(id);
    return {
      id,
      name: rec?.name ?? '',
      index,
      blocked: self.has(id) ? 'self' : loop.has(id) ? 'loop' : null,
    };
  });
}

/**
 * N LINES name pattern: `{i}` = position in the selection (0-based, export order), `{n}` = 1-based, `{name}` = the
 * current name. A pattern without a token gives every line the same name.
 */
export function applyNamePattern(pattern: string, i: number, name: string): string {
  return pattern.replace(/\{(i|n|name)\}/g, (_m, token: string) =>
    token === 'i' ? String(i) : token === 'n' ? String(i + 1) : name,
  );
}

export function hasNameToken(pattern: string): boolean {
  return /\{(i|n|name)\}/.test(pattern);
}

/** Lines in export order (the order N LINES patterns and expressions see). */
export function linesInExportOrder(idx: Pick<DocIndexes, 'lineIndex'>, ids: Iterable<LineId>): LineId[] {
  return [...ids].sort((a, b) => idx.lineIndex(a) - idx.lineIndex(b));
}

/** Features a line uses that the Guided level does not advertise (D§2.4 "in use" chips). */
export interface UsedFeatures {
  /**
   * Layers of the most layered summed property (1 = no extra layers): the layer slots it occupies (highest layer
   * index + 1, as RPE and checker PH-25 count them) or its number of tracks, whichever is larger.
   */
  readonly layers: number;
  readonly color: boolean;
  readonly scale: boolean;
  readonly incline: boolean;
  readonly controls: boolean;
  readonly text: boolean;
  readonly paint: boolean;
  readonly gif: boolean;
  readonly uiMover: boolean;
}

export function usedFeatures(
  doc: Pick<ChartDoc, 'keys' | 'tracks'>,
  idx: Pick<DocIndexes, 'tracksOfLine' | 'keysByTrack'>,
  line: Line,
): UsedFeatures {
  const tracks = idx.tracksOfLine(line.id);
  let layers = 1;
  for (const [prop, ids] of tracks) {
    if (!PROP_SPECS[prop].layered) continue;
    layers = Math.max(layers, ids.length);
    for (const id of ids) {
      const owner = doc.tracks.get(id)?.owner;
      if (owner?.kind === 'line') layers = Math.max(layers, owner.layer + 1);
    }
  }
  const has = (p: LineProp): boolean => (tracks.get(p)?.length ?? 0) > 0;
  let incline = false;
  for (const t of tracks.get('incline') ?? []) {
    for (const k of idx.keysByTrack(t)) {
      const key = doc.keys.get(k);
      if (key !== undefined && (key.vIn !== 0 || key.vOut !== 0)) incline = true;
    }
  }
  const c = line.controls;
  const controls = [c.pos, c.alpha, c.size, c.y].some((curve) => curve !== null && !controlHasNoEffect(curve));
  return {
    layers,
    color: has('color'),
    scale: has('scaleX') || has('scaleY'),
    incline,
    controls,
    text: has('text'),
    paint: has('paint'),
    gif: has('gif') || line.gifAutoLoop,
    uiMover: line.attachUI !== null,
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Keys and segments
// ---------------------------------------------------------------------------------------------------------------

export interface KeyContext {
  readonly key: Key;
  readonly track: Track;
  /** Keys of the track sorted by beat. */
  readonly keys: readonly Key[];
  readonly index: number;
  readonly prev: Key | null;
  readonly next: Key | null;
  /** The line property, or null for effect / video variables. */
  readonly prop: LineProp | null;
  readonly line: LineId | null;
  readonly layer: number;
}

export function keyContext(
  doc: Pick<ChartDoc, 'keys' | 'tracks'>,
  idx: Pick<DocIndexes, 'keysByTrack'>,
  id: KeyId,
): KeyContext | null {
  const key = doc.keys.get(id);
  if (key === undefined) return null;
  const track = doc.tracks.get(key.track);
  if (track === undefined) return null;
  const keys = idx
    .keysByTrack(track.id)
    .map((k) => doc.keys.get(k))
    .filter((k): k is Key => k !== undefined);
  const index = keys.findIndex((k) => k.id === id);
  const owner = track.owner;
  return {
    key,
    track,
    keys,
    index,
    prev: index > 0 ? (keys[index - 1] ?? null) : null,
    next: keys[index + 1] ?? null,
    prop: owner.kind === 'line' ? owner.prop : null,
    line: owner.kind === 'line' ? owner.line : null,
    layer: owner.kind === 'line' ? owner.layer : 0,
  };
}

/** Value the segment starting at `start` arrives at (the next key's arrive value), or null for the last key. */
export function segmentEndValue(ctx: KeyContext): Key['vIn'] | null {
  return ctx.next === null ? null : ctx.next.vIn;
}

export type TweenFamily = NonNullable<ReturnType<typeof Ease.catalog>>['family'];

export function easeFamily(e: EaseValue): TweenFamily | 'hold' | 'bezier' {
  if (e.kind === 'hold') return 'hold';
  if (e.kind === 'bezier') return 'bezier';
  return Ease.catalog(e.id)?.family ?? 'linear';
}

export function isWholeCurve(e: EaseValue): boolean {
  return e.kind !== 'tween' || (e.left === 0 && e.right === 1);
}

export function isLinear(e: EaseValue): boolean {
  return e.kind === 'tween' && e.id === 1 && isWholeCurve(e);
}

export interface SegmentExactness {
  /** 'baked': eased legacy speed, written as linear pieces; 'nan': the slice makes Phira divide by zero. */
  readonly phira: 'exact' | 'baked' | 'nan';
  /** 'differs': RPE draws this easing differently (23, 29); 'ignored': RPE uses a speed easing Phira ignores. */
  readonly rpe: 'exact' | 'differs' | 'baked' | 'ignored';
  /**
   * A sliced Expo / Elastic curve that came from a split (a neighbouring segment holds the adjoining slice of the
   * same curve): within 0.1 % of the original curve (D§6.4). A slice imported on its own is exact in Phira.
   */
  readonly approxSplit: boolean;
}

/** The segments around a key's segment: the one ending at the key (`prev`) and the one after it (`next`). */
export interface SegmentNeighbours {
  readonly prev: Key | null;
  readonly next: Key | null;
}

const SLICE_EPS = 1e-9;

/** Whether `other`'s ease is the same tween curve as `e`, sliced so that it adjoins `e` on the given side. */
function adjoiningSlice(
  e: Extract<EaseValue, { kind: 'tween' }>,
  other: Key | null,
  side: 'before' | 'after',
): boolean {
  const o = other?.ease;
  if (o?.kind !== 'tween' || o.id !== e.id) return false;
  return side === 'before' ? Math.abs(o.right - e.left) <= SLICE_EPS : Math.abs(o.left - e.right) <= SLICE_EPS;
}

export function segmentExactness(
  key: Key,
  prop: LineProp | null,
  speedMode: 'legacy' | 'rpe170',
  around: SegmentNeighbours = { prev: null, next: null },
): SegmentExactness {
  const e = key.ease;
  let phira: SegmentExactness['phira'] = 'exact';
  let rpe: SegmentExactness['rpe'] = 'exact';
  let approxSplit = false;
  if (e.kind === 'tween') {
    if (!isWholeCurve(e) && Ease.sliceIsDegenerate(e.id, e.left, e.right)) phira = 'nan';
    const entry = Ease.catalog(e.id);
    if (entry?.differsInRpe === true) rpe = 'differs';
    if (
      (entry?.family === 'expo' || entry?.family === 'elastic') &&
      !isWholeCurve(e) &&
      ((e.left > 0 && adjoiningSlice(e, around.prev, 'before')) ||
        (e.right < 1 && adjoiningSlice(e, around.next, 'after')))
    ) {
      approxSplit = true;
    }
    if (prop === 'speed' && speedMode === 'legacy' && !isLinear(e) && phira === 'exact') {
      phira = 'baked';
      rpe = 'baked';
    }
  }
  if (prop === 'speed' && speedMode === 'legacy' && key.src?.ignoredEase !== undefined) rpe = 'ignored';
  return { phira, rpe, approxSplit };
}

/** RPE easing id a key's ease is written with (1 for hold / bezier, else the tween id). */
export function rpeEasingId(e: EaseValue): number {
  return e.kind === 'tween' ? e.id : 1;
}

/** Values of keys for display (numbers in time order). */
export function numberKeys(keys: readonly Key[]): readonly Key<number>[] {
  return keys.filter((k): k is Key<number> => typeof k.vOut === 'number' && typeof k.vIn === 'number');
}

/**
 * Display value of a number track at chart time `sec` (DESIGN.md §6.2 in f64): the outgoing segment eases from
 * `vOut` to the next key's `vIn` with progress in seconds; after the last key its `vOut`; before the first key the
 * first value (display only: rendering uses the engine).
 */
export function numberAt(keys: readonly Key[], tempo: TempoMap, sec: Sec): number | null {
  const ks = numberKeys(keys);
  const first = ks[0];
  if (first === undefined) return null;
  if (sec < tempo.secAt(first.t)) return first.vOut;
  for (let i = 0; i < ks.length; i++) {
    const k = ks[i] as Key<number>;
    const next = ks[i + 1];
    if (next === undefined) return k.vOut;
    const t0 = tempo.secAt(k.t);
    const t1 = tempo.secAt(next.t);
    if (sec >= t0 && sec < t1) {
      const p = t1 > t0 ? (sec - t0) / (t1 - t0) : 0;
      return k.vOut + (next.vIn - k.vOut) * Ease.modelY(k.ease, p);
    }
  }
  return ks[ks.length - 1]?.vOut ?? null;
}

/** The key whose segment is active at `beat` (last key at or before it), or the first key before the track. */
export function keyAtOrBefore(keys: readonly Key[], beat: Beat): Key | null {
  let best: Key | null = null;
  for (const k of keys) {
    if (Beats.lte(k.t, beat)) best = k;
    else break;
  }
  return best ?? keys[0] ?? null;
}

/** The key exactly at `beat`, if any. */
export function keyAt(keys: readonly Key[], beat: Beat): Key | null {
  return keys.find((k) => Beats.eq(k.t, beat)) ?? null;
}

/** Keys of a track sorted by beat. */
export function trackKeys(
  doc: Pick<ChartDoc, 'keys'>,
  idx: Pick<DocIndexes, 'keysByTrack'>,
  track: TrackId | undefined,
): Key[] {
  if (track === undefined) return [];
  return idx
    .keysByTrack(track)
    .map((k) => doc.keys.get(k))
    .filter((k): k is Key => k !== undefined);
}

// ---------------------------------------------------------------------------------------------------------------
// Text segments (DESIGN.md §6.8, F§9.3)
// ---------------------------------------------------------------------------------------------------------------

export type TextMode = TextSegmentMode;
export const TEXT_MODES: readonly TextMode[] = ['switch', 'typeOn', 'erase', 'extend', 'counter'];

/**
 * The mode of a text segment from `start` (k.vOut) to `end` (next.vIn) with its curve (F§9.3 cases; a Stay
 * segment is a switch). Same derivation as Core.lines.setTextSegmentMode.
 */
export function textModeOf(start: string, end: string, ease: EaseValue = LINEAR): TextMode {
  return textSegmentMode(start, end, ease);
}

const NUMBER_RE = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

/** The number of a counter string (`%P%12.5` -> 12.5), or null when the rest is not a plain number. */
export function counterValue(s: string): number | null {
  const body = s.split('%P%').join('').trim();
  if (!NUMBER_RE.test(body)) return null;
  const v = Number(body);
  return Number.isFinite(v) ? v : null;
}

/** Decimals Phira shows for a counter (0 when both ends are whole numbers, else 3). */
export function counterDecimals(from: number, to: number): 0 | 3 {
  return Number.isInteger(Math.fround(from)) && Number.isInteger(Math.fround(to)) ? 0 : 3;
}

/** Plain text of a counter end (number written without exponent). */
export function counterText(v: number): string {
  return Number.isInteger(v) ? String(v) : String(Number(v.toPrecision(12)));
}

/**
 * Guard state of a text segment: reveal / erase with an overshooting curve crashes Phira (fatal PH-03); extend with
 * overshoot is allowed with a warning (PH-16).
 */
export function textGuard(mode: TextMode, ease: EaseValue): 'crash' | 'overshootWarning' | null {
  if (!Ease.overshoots(ease)) return null;
  if (mode === 'typeOn' || mode === 'erase') return 'crash';
  if (mode === 'extend') return 'overshootWarning';
  return null;
}

// ---------------------------------------------------------------------------------------------------------------
// Note controls (F§12)
// ---------------------------------------------------------------------------------------------------------------

export type ControlKey = 'pos' | 'alpha' | 'size' | 'y';
export const CONTROL_KEYS: readonly ControlKey[] = ['alpha', 'size', 'pos', 'y'];

/** Phira ignores a control that is empty or has 2 points with a first easing of 1 and a first value of ~1. */
export function controlIsInert(c: ControlCurve): boolean {
  if (c.points.length === 0) return true;
  const p0 = c.points[0] as ControlPoint;
  return c.points.length === 2 && p0.easing === 1 && Math.abs(p0.value - 1) <= 1e-4;
}

/** Every value is 1: the curve changes nothing even when Phira evaluates it (e.g. RPE's default control). */
export function controlIsNeutral(c: ControlCurve): boolean {
  return c.points.every((p) => Math.abs(p.value - 1) <= 1e-4);
}

export function controlHasNoEffect(c: ControlCurve): boolean {
  return controlIsInert(c) || controlIsNeutral(c);
}

/** Inert while it would change something: the case the "add a third point" fix is for. */
export function controlNeedsThirdPoint(c: ControlCurve): boolean {
  return c.points.length === 2 && controlIsInert(c) && !controlIsNeutral(c);
}

/**
 * Value of a control curve as authored at note distance `x` (F§12): each point's easing governs the interval that
 * ENDS at it; extrapolated before the first point along the first interval, the last value after the last point.
 */
export function controlCurveAt(c: ControlCurve, x: number): number {
  const pts = c.points;
  const first = pts[0];
  if (first === undefined) return 1;
  const second = pts[1];
  if (second === undefined) return first.value;
  const segment = (a: ControlPoint, b: ControlPoint): number => {
    const span = b.x - a.x;
    const p = span > 0 ? (x - a.x) / span : 1;
    return a.value + (b.value - a.value) * Ease.staticY(Ease.rpeToTweenId(b.easing), p);
  };
  if (x < first.x) return segment(first, second);
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i] as ControlPoint;
    const b = pts[i + 1] as ControlPoint;
    if (x >= a.x && x < b.x) return segment(a, b);
  }
  return (pts[pts.length - 1] as ControlPoint).value;
}

/** Multiplier Phira applies at note distance `x`: 1 for inert controls (Phira ignores them), else the curve. */
export function controlValueAt(c: ControlCurve, x: number): number {
  return controlIsInert(c) ? 1 : controlCurveAt(c, x);
}

/** The fix for an inert 2-point control: a copy of the last point 1 px further (shape unchanged, now 3 points). */
export function withThirdPoint(c: ControlCurve): ControlCurve {
  const last = c.points[c.points.length - 1];
  if (last === undefined) return c;
  return { points: [...c.points, { x: last.x + 1, easing: 1, value: last.value }] };
}

export type ControlPresetId = 'fadeInNear' | 'fadeOutNear' | 'shrinkFar' | 'centreFar' | 'compressFar';

export interface ControlPreset {
  readonly id: ControlPresetId;
  readonly control: ControlKey;
  readonly curve: ControlCurve;
}

const pts = (list: ReadonlyArray<readonly [number, number]>): ControlCurve => ({
  points: list.map(([x, value]) => ({ x, easing: 1, value })),
});

/** Presets (3 points each, so Phira never ignores them). x = note distance from the line in RPE-y px. */
export const CONTROL_PRESETS: readonly ControlPreset[] = [
  {
    id: 'fadeInNear',
    control: 'alpha',
    curve: pts([
      [0, 1],
      [300, 1],
      [700, 0],
    ]),
  },
  {
    id: 'fadeOutNear',
    control: 'alpha',
    curve: pts([
      [0, 0],
      [150, 0],
      [450, 1],
      [900, 1],
    ]),
  },
  {
    id: 'shrinkFar',
    control: 'size',
    curve: pts([
      [0, 1],
      [300, 1],
      [900, 0.4],
    ]),
  },
  {
    id: 'centreFar',
    control: 'pos',
    curve: pts([
      [0, 1],
      [300, 1],
      [900, 0.3],
    ]),
  },
  {
    id: 'compressFar',
    control: 'y',
    curve: pts([
      [0, 1],
      [300, 1],
      [900, 0.6],
    ]),
  },
];

/** A neutral 3-point control (no effect, never ignored): the starting point of a new distance effect. */
export const NEUTRAL_CONTROL: ControlCurve = pts([
  [0, 1],
  [450, 1],
  [900, 1],
]);

/** Sorted copy of control points (by x). */
export function sortedPoints(points: readonly ControlPoint[]): ControlPoint[] {
  return [...points].sort((a, b) => a.x - b.x);
}

// ---------------------------------------------------------------------------------------------------------------
// Song-select preview range (PH-36)
// ---------------------------------------------------------------------------------------------------------------

/** Margin under the decoded music length (browser MP3 decoders may report padding Phira does not decode). */
export const PREVIEW_MARGIN_SEC = 0.5;
/** Phira's default preview length when previewEnd is null. */
export const PREVIEW_DEFAULT_SEC = 15;

export type PreviewIssue =
  | { readonly kind: 'start' }
  | { readonly kind: 'order' }
  /** Phira refuses to open the chart: (end ?? start + 1) exceeds the song length minus the margin. */
  | { readonly kind: 'beyondSong'; readonly limit: number };

export function previewRangeIssue(start: number, end: number | null, durationSec: number | null): PreviewIssue | null {
  if (!(Number.isFinite(start) && start >= 0)) return { kind: 'start' };
  if (end !== null && !(Number.isFinite(end) && end > start)) return { kind: 'order' };
  if (durationSec !== null && Number.isFinite(durationSec) && durationSec > 0) {
    const limit = durationSec - PREVIEW_MARGIN_SEC;
    if ((end ?? start + 1) > limit) return { kind: 'beyondSong', limit };
  }
  return null;
}

/**
 * A song-select preview range that fits a song of `durationSec` (Phira's load check with the 0.5 s margin):
 * keeps the length when possible (15 s when no end is set), moves the start earlier only as far as needed and
 * always writes an explicit end. Values rounded to milliseconds.
 */
export function clampPreviewRange(
  start: number,
  end: number | null,
  durationSec: number,
): { readonly start: number; readonly end: number } {
  const limit = Math.max(0, durationSec - PREVIEW_MARGIN_SEC);
  const ms = (v: number): number => Math.round(v * 1000) / 1000;
  const s0 = Number.isFinite(start) && start >= 0 ? start : 0;
  const wanted = end !== null && Number.isFinite(end) && end > s0 ? end - s0 : PREVIEW_DEFAULT_SEC;
  const len = Math.min(wanted, limit);
  let s1 = Math.min(s0, Math.max(0, limit - len));
  let e1 = Math.min(s1 + len, limit);
  if (!(e1 > s1)) {
    s1 = 0;
    e1 = limit > 0 ? limit : 0.001;
  }
  s1 = ms(s1);
  e1 = Math.floor(e1 * 1000) / 1000;
  return { start: s1, end: e1 > s1 ? e1 : ms(s1 + 0.001) };
}

/** Decoded length of the project music: the loaded music when it is the project's, else the asset's metadata. */
export function musicDuration(
  doc: Pick<ChartDoc, 'meta' | 'assets'>,
  loaded: { readonly asset: string; readonly durationSec: number } | null,
): number | null {
  const music = doc.meta.music;
  if (music === null) return null;
  if (loaded !== null && loaded.asset === music && loaded.durationSec > 0) return loaded.durationSec;
  const d = doc.assets.get(music)?.meta?.durationSec;
  return d !== undefined && d > 0 ? d : null;
}

/** Speed keys that keep an RPE easing Phira ignores in Classic mode (asked about when switching to RPE 1.7). */
export function ignoredEaseCount(doc: Pick<ChartDoc, 'keys' | 'tracks'>): number {
  let n = 0;
  for (const k of doc.keys.values()) {
    if (k.src?.ignoredEase === undefined) continue;
    const tr = doc.tracks.get(k.track);
    if (tr?.owner.kind === 'line' && tr.owner.prop === 'speed') n++;
  }
  return n;
}

// ---------------------------------------------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------------------------------------------

export function unitContext(
  doc: Pick<ChartDoc, 'assets'>,
  idx: Pick<DocIndexes, 'tracksOfLine'>,
  line: Line,
): UnitContext {
  return { kind: kindOfLine(doc, idx, line), uiMover: line.attachUI !== null };
}

/** Rounds away binary noise for display and storage of converted values (12 significant digits). */
export function tidy(v: number): number {
  if (!Number.isFinite(v)) return v;
  const r = Number(v.toPrecision(12));
  return Object.is(r, -0) ? 0 : r;
}

/** Hex string "#rrggbb" of an RGB triple (tags, marker colours). */
export function rgbToHex(c: RGB): string {
  return `#${c
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** RGB triple of "#rgb" / "#rrggbb" (null for anything else). */
export function hexToRgb(s: string | null): RGB | null {
  if (s === null) return null;
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s.trim());
  if (m === null) return null;
  const h = m[1] as string;
  const full = h.length === 3 ? h.replace(/./g, (c) => c + c) : h;
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
}

/** Neutral eases used by fixes. */
export const EASES = { LINEAR, HOLD } as const;

// ---------------------------------------------------------------------------------------------------------------
// Selections
// ---------------------------------------------------------------------------------------------------------------

/** True when nothing but lines (or nothing at all) is selected: the line inspector shows the active line. */
export function onlyLinesSelected(sel: Selection): boolean {
  return (
    sel.notes.size === 0 &&
    sel.keys.size === 0 &&
    sel.segments.size === 0 &&
    sel.bpm.size === 0 &&
    sel.markers.size === 0 &&
    sel.effects.size === 0 &&
    sel.videos.size === 0
  );
}

/** Sorted ids of a selection set (stable entity keys and command arguments). */
export function sortedIds<T extends number>(set: ReadonlySet<T>): T[] {
  return [...set].sort((a, b) => a - b);
}

/** "#3 Main" (export index and name). */
export function lineLabel(doc: Pick<ChartDoc, 'lines'>, idx: Pick<DocIndexes, 'lineIndex'>, id: LineId): string {
  const line = doc.lines.get(id);
  return `#${String(idx.lineIndex(id))}${line === undefined || line.name === '' ? '' : ` ${line.name}`}`;
}

// ---------------------------------------------------------------------------------------------------------------
// Line properties (absent tracks, "in use", note tilt)
// ---------------------------------------------------------------------------------------------------------------

/** Order of the line property list. */
export const PROPERTY_ORDER: readonly LineProp[] = [
  'moveX',
  'moveY',
  'rotate',
  'alpha',
  'speed',
  'scaleX',
  'scaleY',
  'color',
  'text',
  'paint',
  'gif',
  'incline',
];

export interface PropRow {
  readonly prop: LineProp;
  /** Tracks (layers) of the property. */
  readonly layers: number;
  /** Keys over all layers. */
  readonly keys: number;
  /** Phira uses the property on this line kind. */
  readonly applies: boolean;
}

/** The properties of a line with their track counts, in display order (only those that apply or exist). */
export function propRows(idx: Pick<DocIndexes, 'tracksOfLine' | 'keysByTrack'>, line: Line, kind: LineKind): PropRow[] {
  const tracks = idx.tracksOfLine(line.id);
  const out: PropRow[] = [];
  for (const prop of PROPERTY_ORDER) {
    const ids = tracks.get(prop) ?? [];
    let keys = 0;
    for (const t of ids) keys += idx.keysByTrack(t).length;
    const applies = propAppliesTo(prop, kind);
    // Kind-defining properties of other kinds are not offered (a text track on a normal line makes it a text line).
    const kindProp = prop === 'text' || prop === 'paint' || prop === 'gif';
    if (ids.length === 0 && (!applies || kindProp)) continue;
    out.push({ prop, layers: ids.length, keys, applies });
  }
  return out;
}

/** Value written by the absent-track fix ("Set 100 %", "Set 10"), or null when the absent meaning needs no fix. */
export function absentFixValue(prop: LineProp, uiMover: boolean, hasNotes: boolean): number | null {
  if (prop === 'alpha') return 255;
  if (prop === 'speed' && hasNotes && !uiMover) return 10;
  return null;
}

/** Note tilt as Phira uses it: frozen at its t = 0 value (F§9.4). */
export function inclineInfo(keys: readonly Key[]): {
  readonly animated: boolean;
  readonly frozen: number;
  readonly keys: number;
} {
  const nums = numberKeys(keys);
  const first = nums[0];
  if (first === undefined) return { animated: false, frozen: 0, keys: 0 };
  const atZero = Beats.eq(first.t, Beats.ZERO);
  const animated = nums.length > 1 || !atZero || first.vIn !== first.vOut;
  return { animated, frozen: atZero ? first.vOut : 0, keys: nums.length };
}

/** Display value of a summed property at `sec` (null when the line has no track for it). */
export function sumAt(
  doc: Pick<ChartDoc, 'keys'>,
  idx: Pick<DocIndexes, 'tracksOfLine' | 'keysByTrack'>,
  tempo: TempoMap,
  line: LineId,
  prop: LineProp,
  sec: Sec,
): number | null {
  const ids = idx.tracksOfLine(line).get(prop) ?? [];
  if (ids.length === 0) return null;
  let sum = 0;
  for (const t of ids) {
    const v = numberAt(trackKeys(doc, idx, t), tempo, sec);
    if (v !== null) sum += v;
  }
  return sum;
}

/** Line types offered by the Type dropdown at a level (Guided: Line, Text, Image; plus the current type). */
export function lineTypeOptions(level: 'guided' | 'full', current: LineTypeChoice | null): LineTypeChoice[] {
  return LINE_TYPES.filter((t) => level === 'full' || GUIDED_LINE_TYPES.has(t) || t === current);
}

/** Lines of the selection grouped by type (N LINES header "4 line, 2 image"). */
export function typeCounts(types: readonly LineTypeChoice[]): ReadonlyArray<readonly [LineTypeChoice, number]> {
  const counts = new Map<LineTypeChoice, number>();
  for (const t of types) counts.set(t, (counts.get(t) ?? 0) + 1);
  return LINE_TYPES.filter((t) => counts.has(t)).map((t) => [t, counts.get(t) ?? 0] as const);
}

// ---------------------------------------------------------------------------------------------------------------
// Easing uses
// ---------------------------------------------------------------------------------------------------------------

/** Guard context of a segment for the easing picker and setEase (EaseUse). */
export function easeUseFor(
  prop: LineProp | null,
  owner: 'line' | 'effectVar' | 'videoVar',
  speedMode: 'legacy' | 'rpe170',
  level: 'guided' | 'full',
  textMode: TextMode | null,
): EaseUse {
  if (owner !== 'line') return 'extraVar';
  if (prop === 'speed') {
    if (speedMode === 'rpe170') return 'speed170';
    return level === 'guided' ? 'speedLegacyGuided' : 'speedLegacy';
  }
  if (prop === 'text') return textMode === 'typeOn' || textMode === 'erase' ? 'textRevealErase' : 'textOther';
  return 'general';
}

/** Plain-word strength keys of the easing catalogue (for Guided curve names). */
export function easeCatalogEntry(e: EaseValue): ReturnType<typeof Ease.catalog> {
  return e.kind === 'tween' ? Ease.catalog(e.id) : null;
}
